import {
  requestFunctionJson,
  type FunctionRequestOptions,
} from './functionsTransport';

type Envelope = {success?: boolean; error?: string; [key: string]: unknown};
type PublicSettingsResponse = {success: boolean; settings: Record<string, unknown>};

let publicSettingsPromise: Promise<PublicSettingsResponse> | null = null;

async function request<T extends Envelope>(
  path: string,
  options: FunctionRequestOptions = {},
  authenticated = true,
): Promise<T> {
  return requestFunctionJson<T>(path, {
    ...options,
    auth: authenticated ? 'current-user' : 'public',
  });
}

const write = (
  path: string,
  method: NonNullable<FunctionRequestOptions['method']>,
  body?: unknown,
) => request(path, {method, body});

export const settingsApi = {
  publicGet: () => {
    if (!publicSettingsPromise) {
      publicSettingsPromise = request<PublicSettingsResponse>('settings', {}, false)
        .catch(error => {
          publicSettingsPromise = null;
          throw error;
        });
    }
    return publicSettingsPromise;
  },
  adminGet: () => request<{success: boolean; settings: Record<string, unknown>}>('settings/admin'),
  save: async (settings: Record<string, unknown>) => {
    const response = await write('settings/admin', 'PUT', settings);
    publicSettingsPromise = null;
    return response;
  },
};

export type PlanningResource = 'visits' | 'locations' | 'vehicles';

export const monkPlanningApi = {
  list: (year: number) => request<{
    success: boolean;
    visits: Record<string, unknown>[];
    locations: Record<string, unknown>[];
    vehicles: Record<string, unknown>[];
  }>(`monk-planning?year=${encodeURIComponent(year)}`),
  create: (resource: PlanningResource, data: Record<string, unknown>) =>
    write(`monk-planning/${resource}`, 'POST', data),
  update: (resource: PlanningResource, id: string, data: Record<string, unknown>) =>
    write(`monk-planning/${resource}/${encodeURIComponent(id)}`, 'PUT', data),
  remove: (resource: PlanningResource, id: string) =>
    write(`monk-planning/${resource}/${encodeURIComponent(id)}`, 'DELETE'),
};

export const emailTemplatesApi = {
  list: () => request<{success: boolean; templates: Record<string, unknown>[]}>('email-templates'),
  history: (id: string) => request<{success: boolean; versions: Record<string, unknown>[]}>(
    `email-templates/${encodeURIComponent(id)}/versions`,
  ),
  create: (data: Record<string, unknown>) => request<{
    success: boolean;
    template: Record<string, unknown>;
  }>('email-templates', {method: 'POST', body: data}),
  update: (id: string, data: Record<string, unknown>) => request<{
    success: boolean;
    template: Record<string, unknown>;
  }>(`email-templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: data,
  }),
  remove: (id: string) => write(`email-templates/${encodeURIComponent(id)}`, 'DELETE'),
};

export type EmailMonitoringTimestamp = string | {
  seconds?: number;
  _seconds?: number;
  nanoseconds?: number;
  _nanoseconds?: number;
};

export type MonitoredMailData = {
  to: string;
  bcc?: string[];
  message: {
    subject: string;
    html: string;
  };
};

export type EmailFailureRecord = {
  id: string;
  originalTo: string;
  originalSubject: string;
  error: string;
  attemptCount: number;
  failureTime: EmailMonitoringTimestamp;
  originalMailData?: MonitoredMailData;
  resolvedAt?: EmailMonitoringTimestamp;
  resolvedTo?: string;
};

export type EmailSuccessRecord = {
  id: string;
  to: string;
  subject: string;
  deliveryTime: EmailMonitoringTimestamp;
  messageId: string;
  response: string;
  originalMailData?: MonitoredMailData;
};

export type EmailQueueRecord = {
  id: string;
  to: string;
  bcc?: string[];
  message?: {
    subject: string;
    html: string;
  };
  templateName?: string;
  status: 'PENDING' | 'RETRY' | 'QUEUED' | 'SUPPRESSED';
  retryCount: number;
  timestamp: EmailMonitoringTimestamp;
  type: string;
  lastError?: string;
  suppressionReason?: string;
};

type EmailDashboard = {
    historyCursors?: Record<'failures' | 'successes' | 'PENDING' | 'RETRY' | 'SUCCESS' | 'QUEUED' | 'SUPPRESSED', string | null>;
    legacyContentIncomplete?: boolean;
    statistics: {
      totalSent: number;
      totalFailed: number;
      totalPending: number;
      totalRetrying: number;
      successRate: string;
      totalEmails: number;
      lastUpdated: EmailMonitoringTimestamp;
      dailyStats: Record<string, {
        sent: number;
        failed: number;
        pending: number;
      }>;
    };
    recentFailures: {
      count: number;
      failures: EmailFailureRecord[];
    };
    recentSuccesses: {
      count: number;
      successes: EmailSuccessRecord[];
    };
    queue: {
      count: number;
      emails: EmailQueueRecord[];
    };
};

export type EmailDashboardResponse = {
  success: boolean;
  dashboard: EmailDashboard;
};

type EmailDashboardWireResponse = {
  success: boolean;
  dashboard: Omit<EmailDashboard, 'queue'> & {
    queue?: EmailDashboard['queue'];
  };
};

export const emailMonitoringApi = {
  resend: (successId: string, commandId: string) => request<{success: boolean; queueId: string}>(
    'manageEmailQueue', {
      method: 'POST',
      body: {action: 'resend', successId, commandId},
    },
  ),
  getDashboard: async (limit = 100, recipient?: string, cursors?: EmailDashboard['historyCursors']): Promise<EmailDashboardResponse> => {
    const response = await request<EmailDashboardWireResponse>(
      `getEmailDashboard?limit=${encodeURIComponent(limit)}${recipient?.trim() ? `&recipient=${encodeURIComponent(recipient.trim().toLowerCase())}` : ''}${recipient && cursors ? `&cursor=${encodeURIComponent(JSON.stringify({recipient: recipient.trim().toLowerCase(), cursors}))}` : ''}`,
    );
    return {
      ...response,
      dashboard: {
        ...response.dashboard,
        queue: response.dashboard.queue ?? {count: 0, emails: []},
      },
    };
  },
  retryFailed: (failureId: string, recipient?: string) => request<{
    success: boolean;
    message: string;
    originalFailureId: string;
    status: 'queued' | 'sent';
  }>('retryFailedEmail', {
    method: 'POST',
    body: {failureId, ...(recipient?.trim() ? {recipient: recipient.trim()} : {})},
  }),
  manageRetryQueue: (
    action: 'retry' | 'cancel',
    emailIds: string[],
  ) => request<{
    success: boolean;
    action: 'retry' | 'cancel';
    results: {
      successful: number;
      failed: number;
      skipped: number;
      total: number;
    } | {
      cancelled: number;
      skipped: number;
      total: number;
    };
  }>('manageRetryQueue', {
    method: 'POST',
    body: {action, emailIds},
  }),
  deleteEmails: (queueIds: string[], failureIds: string[]) => request<{
    success: boolean;
    action: 'delete';
    results: {
      deletedQueue: number;
      deletedFailures: number;
      total: number;
    };
  }>('manageRetryQueue', {
    method: 'POST',
    body: {action: 'delete', queueIds, failureIds},
  }),
};
